import { Link } from '@tanstack/react-router'
import { create } from '@bufbuild/protobuf'
import { ChevronDown, Copy, FilePlus, LayoutTemplate, Plus } from 'lucide-react'
import { toast } from 'sonner'

import { DuplicateQuoteRequestSchema } from '@/gen/cortado/v1/cortado_pb'
import type { Quote } from '@/gen/cortado/v1/invoices_pb'
import { useDuplicateQuote, useQuoteLitesByIds } from '@/stores/quotes.store'
import { useListQuoteTemplates } from '@/stores/organization.store'
import { getErrorDescription } from '@/lib/error-utils'
import { reportError } from '@/lib/error-reporting'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { jobDetailSortQuotes } from '@/components/pipeline/job-detail-view'
import { NEW_QUOTE_FROM_REQUEST, NEW_QUOTE_ID } from '@/components/pipeline/pipeline-quote-route'

interface NewQuoteMenuProps {
  jobId: string
  organizationId: string
  /** The inquiry's event type; its active template is listed first. */
  eventTypeKey?: string
  /** The job's quotes, each offered as a copy. */
  quoteIds: string[]
  acceptedQuoteId?: string
  /** False when the job has no inquiry to draft a new quote from. */
  canDraft: boolean
  /** Fired with the copy once "Copy of #n" creates it. */
  onDuplicated: (copy: Quote) => void
  /** `ghost` is the quiet text-style "+ New quote" trigger in the Quotes card header. */
  variant?: 'brand' | 'outline' | 'ghost'
  /** The trigger's label; "Draft quote" for a lead with no quote yet. */
  label?: string
}

/**
 * The pipeline's one way to start a quote. A new quote can start from any
 * active template (the one for the inquiry's event type first), from the
 * request alone (its events and the items the lead picked), or as a copy of a
 * quote already on the job.
 */
export function NewQuoteMenu({
  jobId,
  organizationId,
  eventTypeKey,
  quoteIds,
  acceptedQuoteId,
  canDraft,
  onDuplicated,
  variant = 'outline',
  label = 'New quote',
}: NewQuoteMenuProps) {
  const { quoteTemplates } = useListQuoteTemplates(organizationId)
  const { quotes } = useQuoteLitesByIds(quoteIds)
  const { duplicateQuote, isLoading: isDuplicating } = useDuplicateQuote()

  const templates = quoteTemplates
    .filter((template) => template.isActive)
    // The event type's own template first; the rest keep the order Settings lists them in.
    .sort((a, b) => Number(isMatch(b)) - Number(isMatch(a)))
  function isMatch(template: { eventTypeKey?: string }) {
    return !!eventTypeKey && template.eventTypeKey === eventTypeKey
  }

  async function copyQuote(id: string) {
    try {
      const response = await duplicateQuote(create(DuplicateQuoteRequestSchema, { id }))
      if (response.quote) {
        toast.success(`Copied as Quote #${response.quote.quoteNumber}`)
        onDuplicated(response.quote)
      }
    } catch (error) {
      reportError(error, { feature: 'pipeline_new_quote_copy', kind: 'mutation' })
      toast.error('Failed to copy quote', { description: getErrorDescription(error) })
    }
  }

  const newQuoteLink = (start: string) => ({
    to: '/pipeline/$id/quotes/$quoteId' as const,
    params: { id: jobId, quoteId: NEW_QUOTE_ID },
    search: { start },
  })

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          size="sm"
          variant={variant}
          className={cn(
            'shrink-0',
            variant === 'ghost' && '-my-1 -mr-2 h-7 gap-1 px-2 text-muted-foreground hover:text-foreground',
          )}
          disabled={isDuplicating}
        >
          <Plus className="h-4 w-4" />
          {label}
          {variant !== 'ghost' && <ChevronDown className="h-3.5 w-3.5 opacity-60" />}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        <DropdownMenuLabel className="text-xs text-muted-foreground">Start from</DropdownMenuLabel>
        {templates.map((template) => (
          <DropdownMenuItem key={template.id} asChild disabled={!canDraft}>
            <Link {...newQuoteLink(template.id)}>
              <LayoutTemplate className="h-4 w-4" />
              <span className="min-w-0 flex-1">
                <span className="block truncate">{template.title || 'Untitled template'}</span>
                {isMatch(template) && <span className="block text-xs text-muted-foreground">Matches this event type</span>}
              </span>
            </Link>
          </DropdownMenuItem>
        ))}
        <DropdownMenuItem asChild disabled={!canDraft}>
          <Link {...newQuoteLink(NEW_QUOTE_FROM_REQUEST)}>
            <FilePlus className="h-4 w-4" />
            <span className="min-w-0 flex-1">
              <span className="block">The request</span>
              <span className="block text-xs text-muted-foreground">Its events and the items the lead picked</span>
            </span>
          </Link>
        </DropdownMenuItem>
        {quotes.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs text-muted-foreground">Copy a quote</DropdownMenuLabel>
            {jobDetailSortQuotes(quotes, acceptedQuoteId).map((lite) => (
              <DropdownMenuItem key={lite.id} disabled={isDuplicating} onSelect={() => void copyQuote(lite.id)}>
                <Copy className="h-4 w-4" />
                <span className="min-w-0 flex-1 truncate">
                  Copy of #{lite.quoteNumber}
                  {lite.title && <span className="text-muted-foreground"> · {lite.title}</span>}
                </span>
              </DropdownMenuItem>
            ))}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
